import React, { FormEvent, useState, useRef, useEffect } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import '../../Plans/modals/plan_createplan.css';
import { PlanFeatureResponse, UpdatePlanFeatureRequest } from '../../../../../interfaces';
import { useAuth } from '../../../../../firebase/AuthProvider';
import { updatePlanFeatureAsync } from '../../../../../services/PlanService/PlanFeatureService';
import { PlanFeatureKey } from '../../../../../Data/SD';
import { AxiosError } from 'axios';

interface EditPlanFeatureModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void | Promise<void>;
    feature: PlanFeatureResponse;
}

const EditPlanFeatureModal: React.FC<EditPlanFeatureModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    feature,
}) => {
    const { currentUser } = useAuth();
    const [form, setForm] = useState<Partial<UpdatePlanFeatureRequest>>({
        featureKey: '',
        isEnabled: true,
        limitValue: null,
        featureName: ''
    });
    const [errors, setErrors] = useState<Partial<Record<keyof UpdatePlanFeatureRequest, string>>>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const controllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (feature) {
            setForm({
                featureKey: feature.featureKey,
                isEnabled: feature.isEnabled,
                limitValue: feature.limitValue,
                featureName: feature.featureName
            });
        }
    }, [feature]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value, type, checked } = e.target as HTMLInputElement;
        setForm((prev) => ({
            ...prev,
            [name]:
                type === 'number'
                    ? value === ''
                        ? null
                        : Number(value)
                    : type === 'checkbox'
                        ? checked
                        : value,
        } as any));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const validateForm = () => {
        const newErrors: Partial<Record<keyof UpdatePlanFeatureRequest, string>> = {};
        if (!form.featureName || !form.featureName.toString().trim()) {
            newErrors.featureName = 'Please enter a feature featureName.';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validateForm()) return;
        if (!currentUser) return;

        const token = await currentUser.getIdToken();
        if (!token) return;

        setIsLoading(true);
        const controller = new AbortController();
        controllerRef.current = controller;

        const payload: UpdatePlanFeatureRequest = {
            featureKey: (form.featureKey as string).trim(),
            isEnabled: Boolean(form.isEnabled),
            limitValue: form.limitValue ?? null,
            featureName: form.featureName ?? ""
        };

        try {
            const res = await updatePlanFeatureAsync(token, feature.id, payload, controller.signal);
            if (res.success) {
                toast.success('Feature updated successfully');
                onSuccess && onSuccess();
                onClose();
            } else {
                toast.error(res.message);
            }
        } catch (error: any) {
            if (error.name === 'AbortError') return;
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to save.");
        } finally {
            setIsLoading(false);
            controllerRef.current = null;
        }
    };

    const handleClose = () => {
        controllerRef.current?.abort();
        controllerRef.current = null;
        setErrors({});
        onClose();
    };

    return (
        <Modal
            show={isOpen}
            onHide={handleClose}
            className="custom_popup"
            centered
            backdrop={isLoading ? 'static' : true}
            keyboard={!isLoading}
        >
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton={!isLoading}>
                    <Modal.Title>Edit Feature</Modal.Title>
                </Modal.Header>
                <Modal.Body>

                    <div className="plan_formGroup">
                        <label className="plan_label" htmlFor="featureName">
                            Feature Name
                        </label>
                        <input
                            id="featureName"
                            name="featureName"
                            type="text"
                            value={form.featureName || ''}
                            onChange={handleInputChange}
                            className="plan_input"
                        />
                        {errors.featureName && <div className="text-red-500 text-sm">{errors.featureName}</div>}
                    </div>

                    <div className="plan_formGroup">
                        <label className="plan_label" htmlFor="featureKey">
                            Feature Key
                        </label>
                        <select
                            id="featureKey"
                            name="featureKey"
                            value={form.featureKey ?? ''}
                            onChange={handleInputChange}
                            className="plan_input"
                        >
                            <option value="">Select Feature</option>

                            {Object.values(PlanFeatureKey).map((key) => (
                                <option key={key} value={key}>
                                    {key}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="plan_formGroup flex items-center gap-2">
                        <input
                            id="isEnabled"
                            name="isEnabled"
                            type="checkbox"
                            checked={Boolean(form.isEnabled)}
                            onChange={handleInputChange}
                        />
                        <label className="plan_label" htmlFor="isEnabled">
                            Enabled
                        </label>
                    </div>

                    <div className="plan_formGroup">
                        <label className="plan_label" htmlFor="limitValue">
                            Limit Value
                        </label>
                        <input
                            id="limitValue"
                            name="limitValue"
                            type="number"
                            min="0"
                            value={form.limitValue ?? ''}
                            onChange={handleInputChange}
                            className="plan_input"
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <Button
                        type="submit"
                        className="px-6 py-2.5 text-sm font-medium rounded-lg bg-[#7E57C2] text-white hover:bg-[#6D4CAB] flex items-center justify-center"
                        disabled={isLoading}
                    >
                        {isLoading ? <Spinner animation="border" size="sm" className="mr-2" /> : 'Update'}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export default EditPlanFeatureModal;
